/** Turkish formatting helpers — purchasing (satınalma) module. */

const qtyFormat = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 })
const amountFormat = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const priceFormat = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
})

/** Korgun currency code → symbol. P_DOV_Tip: EU=EURO, TL=Turkish Lira, US=Dollar. */
const CURRENCY_SYMBOL: Record<string, string> = {
  TL: '₺',
  EU: '€',
  US: '$',
}

/** Korgun `Birim` → the unit label shown next to a quantity. */
const UNIT_LABEL: Record<string, string> = {
  CIFT: 'çift',
  DESI: 'desi',
  ADET: 'adet',
  MT: 'mt',
}

export function currencySymbol(code: string | null): string {
  if (!code) return ''
  return CURRENCY_SYMBOL[code] ?? code
}

export function unitLabel(unit: string | null | undefined): string {
  if (!unit) return ''
  return UNIT_LABEL[unit.trim().toUpperCase()] ?? unit.toLocaleLowerCase('tr-TR')
}

export function formatQty(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return qtyFormat.format(value)
}

export function formatAmount(value: number | null | undefined, code: string | null = null): string {
  if (value === null || value === undefined) return '—'
  const symbol = currencySymbol(code)
  return symbol ? `${amountFormat.format(value)} ${symbol}` : amountFormat.format(value)
}

/**
 * Unit price. Purchase prices run to four decimals (0.7500 $/çift), so they get
 * their own formatter — rounding them to two would turn distinct prices equal.
 */
export function formatPrice(value: number | null | undefined, code: string | null = null): string {
  if (value === null || value === undefined || value === 0) return '—'
  const symbol = currencySymbol(code)
  return symbol ? `${priceFormat.format(value)} ${symbol}` : priceFormat.format(value)
}

/**
 * Money that is zero renders as an em dash, not "0,00 ₺".
 *
 * Only 115 of the 563 purchase orders carry a price at all, so a literal zero
 * would be read as "this costs nothing" instead of "this was never priced".
 */
export function formatAmountOrDash(
  value: number | null | undefined,
  code: string | null = null,
): string {
  if (value === null || value === undefined || value === 0) return '—'
  return formatAmount(value, code)
}

/** API dates are 'YYYY-MM-DD' text; converted to dd.mm.yyyy without going through Date. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const [year, month, day] = iso.slice(0, 10).split('-')
  if (!year || !month || !day) return iso
  return `${day}.${month}.${year}`
}

/** 'YYYY-MM-DD HH:MM' → 'dd.mm.yyyy HH:MM'. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const [datePart, time] = value.split(' ')
  const d = formatDate(datePart ?? null)
  return time ? `${d} ${time}` : d
}

export function percentText(value: number): string {
  return `%${qtyFormat.format(value)}`
}
